import { useQuery } from '@apollo/client';
import React, { useState } from 'react';
import UserPostsList from './UserPostsList';
import UserProfileHeader from './UserProfileHeader';
import * as styles from './UserProfileView.module.scss';
import PageLayout from '../../app/layout/PageLayout';
import SideColumnLayout from '../../components/SideColumnLayout';
import { gql } from '../../graphql/generated/gql';
import {
  UserPostsFilter,
  UserPostsOrder,
} from '../../graphql/generated/graphql';
import GroupIcon from '../../groups/GroupIcon';
import MembershipGroups from '../../groups/MembershipGroups';
import allPostsIcon from '../../icons/non-prefilled/16x16/allPosts.svg';
import orderIcon from '../../icons/non-prefilled/16x16/order.svg';
import postIcon from '../../icons/non-prefilled/16x16/post.svg';
import contributedIcon from '../../icons/non-prefilled/16x16/postWithContent.svg';
import templateIcon from '../../icons/non-prefilled/16x16/template.svg';
import verifiedIcon from '../../icons/non-prefilled/16x16/verified.svg';
import PostFilterMenu from '../../post/PostFilterMenu';
import MembershipTopics from '../../topic/MembershipTopics';
import TopicIcon from '../../topic/TopicIcon';
import useAllTopics from '../../topic/useAllTopics';
import useMembershipTopics from '../../topic/useMembershipTopics';
import useErrorRedirectEffect from '../../utils/error/useErrorRedirectEffect';
import PageMeta from '../../utils/PageMeta';
import useSlug from '../../utils/router/useSlug';

const DEFAULT_FILTER = UserPostsFilter.Published;
const DEFAULT_ORDER = UserPostsOrder.RecentlyPublished;

const USER_PROFILE_QUERY = gql(/** @complexity 8 */ `
  query UserProfileUser($id: ID!) {
    user(id: $id) {
      id
      name
      membershipTopics {
        id
      }
      membershipGroups {
        id
        ...MembershipGroups_groups
      }
      ...UserProfileHeader_user
      allPostsCount: postsCount(filter: ALL)
      contributedPostsCount: postsCount(filter: CONTRIBUTED)
      publishedPostsCount: postsCount(filter: PUBLISHED)
      verifiedPostsCount: postsCount(filter: VERIFIED)
      templatePostsCount: postsCount(filter: TEMPLATE)
    }
  }
`);

const UserProfileView = () => {
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER);
  const [userId] = useSlug('userSlug');

  const { data, error } = useQuery(USER_PROFILE_QUERY, {
    variables: { id: userId },
  });

  useErrorRedirectEffect({ noun: 'user', error });

  const user = data?.user;

  const allTopics = useAllTopics({ flatten: true });

  const membershipTopics = useMembershipTopics(
    allTopics,
    user?.membershipTopics,
  );

  const membershipGroups = user ? user.membershipGroups : null;

  if (!user) return null;

  const shouldShowTopicsMembership = Boolean(membershipTopics);
  const shouldShowGroupsMembership =
    membershipGroups && membershipGroups.length > 0;

  const counts: Record<UserPostsFilter, number> = {
    [UserPostsFilter.All]: user.allPostsCount,
    [UserPostsFilter.Verified]: user.verifiedPostsCount,
    [UserPostsFilter.Contributed]: user.contributedPostsCount,
    [UserPostsFilter.Published]: user.publishedPostsCount,
    [UserPostsFilter.Template]: user.templatePostsCount,
  };

  return (
    <PageLayout.Content noPaddingX noPaddingY>
      <PageMeta
        title={`${user.name} - Slab`}
        desktopTabIcon={user.avatar?.thumb}
      />
      <SideColumnLayout
        aboveAll={<UserProfileHeader user={user} />}
        main={{
          seamless: true,
          title: 'Posts',
          controls: (
            <>
              <PostFilterMenu
                onChange={setFilter}
                options={{
                  [UserPostsFilter.Published]: {
                    icon: postIcon,
                    count: counts.PUBLISHED,
                    label: 'Published',
                  },
                  [UserPostsFilter.Verified]: {
                    icon: verifiedIcon,
                    count: counts.VERIFIED,
                    label: 'Verified',
                  },
                  [UserPostsFilter.Template]: {
                    icon: templateIcon,
                    count: counts.TEMPLATE,
                    label: 'Template',
                  },
                  [UserPostsFilter.Contributed]: {
                    icon: contributedIcon,
                    count: counts.CONTRIBUTED,
                    label: 'Contributed',
                  },
                  [UserPostsFilter.All]: {
                    icon: allPostsIcon,
                    count: counts.ALL,
                    label: 'All posts',
                  },
                }}
                title="Show"
                value={filter}
              />
              <PostFilterMenu
                icon={orderIcon}
                onChange={setOrderBy}
                options={{
                  [UserPostsOrder.RecentlyPublished]: {
                    label: 'Recently published',
                  },
                  [UserPostsOrder.RecentlyUpdated]: {
                    label: 'Recently updated',
                  },
                  [UserPostsOrder.Popularity]: {
                    label: 'Popularity',
                  },
                  [UserPostsOrder.Alphabetical]: {
                    label: 'Alphabetical',
                  },
                }}
                menuPlacement="bottom-end"
                title="Sort"
                value={orderBy}
              />
            </>
          ),
          children: (
            <UserPostsList userId={user.id} filter={filter} orderBy={orderBy} />
          ),
        }}
        sideColumn={
          shouldShowTopicsMembership || shouldShowGroupsMembership
            ? {
                title: shouldShowGroupsMembership
                  ? 'Memberships'
                  : 'Topic membership',
                icon: shouldShowGroupsMembership ? null : <TopicIcon />,
                children: (
                  <>
                    {shouldShowGroupsMembership && (
                      <>
                        <div className={styles.subHeader}>
                          <div className={styles.iconContainer}>
                            <GroupIcon />
                          </div>
                          <h4>Groups</h4>
                        </div>
                        <MembershipGroups membershipGroups={membershipGroups} />
                      </>
                    )}
                    {shouldShowTopicsMembership && (
                      <>
                        {shouldShowGroupsMembership && (
                          <div className={styles.subHeader}>
                            <div className={styles.iconContainer}>
                              <TopicIcon />
                            </div>
                            <h4>Topics</h4>
                          </div>
                        )}
                        <MembershipTopics membershipTopics={membershipTopics} />
                      </>
                    )}
                  </>
                ),
              }
            : undefined
        }
      />
    </PageLayout.Content>
  );
};

export default UserProfileView;
